// ============================================================================
// CRS (Express Entry) assessment copy.
// ============================================================================
// Same policy as the OINP wizard: English is complete and is the per-key
// fallback. Other locales carry navigation and form chrome, plus — for the six
// locales that already had reviewed results copy — the results text too.
//
// What is deliberately NOT machine translated: the option labels that define
// eligibility. "Two or more certificates/diplomas/degrees (one must be 3+
// years)" and "Must have been full-time, at least 8 months, physically in
// Canada" are IRCC criteria, not UI strings. Getting one wrong in a language
// nobody on the team reads is how a candidate picks the wrong option and gets a
// score that is confidently incorrect. Those stay English until reviewed.
// ============================================================================

import { createI18n } from '../i18nCore';

const EN = {
  // ---- chrome ------------------------------------------------------------
  stepOf: 'Step {current} of {total}',
  back: '← Back',
  cancel: '← Cancel',
  next: 'Continue →',
  calculate: 'Calculate Score',
  select: 'Select...',
  score: 'Score',
  yes: 'Yes',
  no: 'No',
  notApplicable: 'Not applicable',
  speaking: 'Speaking',
  listening: 'Listening',
  reading: 'Reading',
  writing: 'Writing',

  // ---- 1 marital ---------------------------------------------------------
  maritalTitle: 'Marital Status',
  maritalSubtitle: 'This determines whether spouse factors apply.',
  maritalQuestion: 'What is your marital status?',
  maritalSingle: 'Never Married / Single',
  maritalMarried: 'Married',
  maritalCommonLaw: 'Common-Law',
  maritalDivorced: 'Divorced / Separated',
  maritalWidowed: 'Widowed',
  maritalAnnulled: 'Annulled Marriage',
  spouseIsCitizen: 'Is your spouse a citizen or PR of Canada?',
  spouseComing: 'Will your spouse come with you to Canada?',

  // ---- 2 age -------------------------------------------------------------
  ageTitle: 'Age',
  ageSubtitle: "Your age at the time you'd be invited to apply.",
  ageQuestion: 'How old are you?',
  ageYears: '{age} years',
  ageYearsOrLess: '{age} years or less',
  ageYearsOrMore: '{age} years or more',

  // ---- 3 education -------------------------------------------------------
  educationTitle: 'Education',
  educationSubtitle: 'Highest level with a Canadian credential or ECA.',
  educationQuestion: 'Level of education:',
  hasCanadianEdu: 'Have you earned a Canadian degree/diploma?',
  hasCanadianEduHelp: 'Must have been full-time, at least 8 months, physically in Canada.',
  canadianEduLevel: 'Canadian education level:',
  canEduSecondary: 'Secondary (high school) or less',
  canEduShort: 'One- or two-year diploma or certificate',
  canEduLong: "Three-year+ degree / Master's / PhD",

  // ---- 4 first language --------------------------------------------------
  firstLangTitle: 'First Language',
  firstLangSubtitle: 'Results must be less than 2 years old.',
  firstLangQuestion: 'Which test did you take?',

  // ---- 5 second language -------------------------------------------------
  secondLangTitle: 'Second Language',
  secondLangSubtitle: 'Optional. Other official language test.',
  secondLangQuestion: 'Did you take a second language test?',
  secondLangWhich: 'Which test?',

  // ---- 6 work ------------------------------------------------------------
  workTitle: 'Work Experience',
  workSubtitle: 'Paid, skilled work at NOC TEER 0, 1, 2 or 3.',
  canadianWork: 'Canadian work experience (last 10 years):',
  foreignWork: 'Foreign work experience (last 10 years):',
  certQual: 'Certificate of qualification from a Canadian province?',
  certQualHelp: 'Trade occupations only. Not the same as PNP.',
  yearsNone: 'None or less than a year',
  years1: '1 year',
  years2: '2 years',
  years3: '3 years',
  years3plus: '3 years or more',
  years4: '4 years',
  years5plus: '5 years or more',
  // Labour Market Integration factor (from November 3, 2026) — IRCC criteria, English until reviewed.
  lmiWorkNoc: 'Occupation of your Canadian work (optional):',
  lmiWorkNocHelp: "The job in which you have at least 12 months (1,560 hours) of that Canadian experience. Occupations on IRCC's high-wage list earn extra points. If your 12 months combine several jobs, your adviser can check it.",
  lmiJobOffer: 'Valid job offer in Canada?',
  lmiJobOfferHelp: 'Generally full-time, continuous work for at least one year after you become a permanent resident. From November 3, 2026 no LMIA is needed. Only offers in a high-wage occupation earn points.',
  lmiJobOfferNoc: 'Occupation of the job offer:',
  lmiNocPlaceholder: 'Type a job title or a 5-digit NOC code',
  lmiUseCode: 'Use NOC {code}',
  lmiSearching: 'Searching…',
  lmiNoResults: 'No matching occupation. Try another title, or enter the 5-digit code.',
  lmiNocLookup: 'Find your NOC code',
  lmiNocNotFound: '{code} is not a NOC 2021 occupation code.',
  lmiTier: "On IRCC's high-wage list: at least {tier} the average median wage",
  lmiNotHighWage: "Not on IRCC's high-wage list — no extra points",
  lmiChange: 'Change',

  // ---- 7 additional ------------------------------------------------------
  additionalTitle: 'Additional Factors',
  additionalSubtitle: 'Provincial nomination, siblings, and other bonus criteria.',
  pnpQuestion: 'Provincial nomination certificate?',
  siblingQuestion: 'Sibling in Canada (18+, citizen or PR)?',

  // ---- 8 spouse ----------------------------------------------------------
  spouseTitle: 'Spouse / Partner',
  spouseSubtitle: "Your spouse's education, language and work affect your score.",
  spouseEducation: "Spouse's education:",
  spouseCanWork: "Spouse's Canadian work (last 10 years):",
  spouseLangTest: "Spouse's language test:",

  // ---- education levels (IRCC definitions — English until reviewed) -------
  eduNone: 'Less than secondary school (high school)',
  eduSecondary: 'Secondary diploma (high school graduation)',
  eduOneYear: 'One-year program at a university, college, trade or technical school',
  eduTwoYear: 'Two-year program at a university, college, trade or technical school',
  eduBachelors: "Bachelor's degree OR three or more year program",
  eduTwoOrMore: 'Two or more certificates/diplomas/degrees (one must be 3+ years)',
  eduMasters: "Master's degree, or professional degree (medicine, law, etc.)",
  eduPhd: 'Doctoral level university degree (PhD)',

  // ---- results -----------------------------------------------------------
  title: 'Your CRS Score',
  subtitle: 'Comprehensive Ranking System estimate',
  strong: 'Strong score — competitive for general draws',
  moderate: 'Moderate score — competitive for category-based draws',
  below: 'Below typical cutoffs — strategies available',
  improve: 'Improvement needed — consider PNP, language, or education',
  coreLabel: 'Core / Human Capital',
  spouseLabel: 'Spouse Factors',
  skillLabel: 'Skill Transferability',
  additionalLabel: 'Additional Points',
  ageLabel: 'Age',
  educationLabel: 'Education',
  firstLangLabel: 'First language',
  secondLangLabel: 'Second language',
  canWorkLabel: 'Canadian work experience',
  lmiLabel: 'Labour Market Integration',
  lmiWorkLengthLabel: 'Canadian work experience (length)',
  lmiHighWageLabel: 'High-wage occupation points',
  lmiJobOfferLabel: 'Job offer points',
  lmiUsedWork: 'Counted: Canadian work experience',
  lmiUsedOffer: 'Counted: job offer (the higher option)',
  spouseEduLabel: 'Education',
  spouseLangLabel: 'Language',
  spouseWorkLabel: 'Canadian work',
  bonusTitle: 'Want to improve your score?',
  siteTitle: 'Which programs could fit you?',
  siteDesc: "iMaven's AI immigration agent picks up from these answers: the programs worth a look and what to check next. Free, general information.",
  siteContinue: 'Continue with the free AI program check',
  bonusDesc: 'Continue to the full consultation and receive a bonus personalized strategy from our licensed immigration consultants.',
  continueBtn: 'Continue to Consultation (+ Bonus)',
  declineBtn: 'Not now, thank you',
  declineNote: 'One of our team members will contact you shortly.',
  disclaimer: 'This tool is for general guidance only. In the event of any discrepancy between these results and official IRCC results, the official results shall govern.',
};

// Locales that already carried reviewed results copy keep it, and gain chrome.
const ES = {
  stepOf: 'Paso {current} de {total}', back: '← Atrás', cancel: '← Cancelar', next: 'Continuar →',
  calculate: 'Calcular puntaje', select: 'Seleccionar...', score: 'Puntaje',
  yes: 'Sí', no: 'No', notApplicable: 'No aplica',
  speaking: 'Expresión oral', listening: 'Comprensión auditiva', reading: 'Lectura', writing: 'Escritura',
  title: 'Tu Puntaje CRS', subtitle: 'Estimación del Sistema de Clasificación Integral',
  strong: 'Puntaje fuerte — competitivo para sorteos generales',
  moderate: 'Puntaje moderado — competitivo para sorteos por categoría',
  below: 'Por debajo de los cortes típicos — hay estrategias disponibles',
  improve: 'Se necesita mejorar — considere PNP, idioma o educación',
  coreLabel: 'Capital Humano Principal', spouseLabel: 'Factores del Cónyuge',
  skillLabel: 'Transferencia de Habilidades', additionalLabel: 'Puntos Adicionales',
  ageLabel: 'Edad', educationLabel: 'Educación', firstLangLabel: 'Primer idioma',
  secondLangLabel: 'Segundo idioma', canWorkLabel: 'Experiencia laboral canadiense',
  spouseEduLabel: 'Educación', spouseLangLabel: 'Idioma', spouseWorkLabel: 'Trabajo canadiense',
  bonusTitle: '¿Quiere mejorar su puntaje?',
  bonusDesc: 'Continúe a la consulta completa y reciba una estrategia personalizada de bonificación de nuestros consultores.',
  continueBtn: 'Continuar a Consulta (+ Bonificación)', declineBtn: 'No ahora, gracias',
  declineNote: 'Uno de nuestros asesores se pondrá en contacto con usted pronto.',
  disclaimer: 'Esta herramienta es solo para orientación general.',
};

const FR = {
  stepOf: 'Étape {current} sur {total}', back: '← Retour', cancel: '← Annuler', next: 'Continuer →',
  calculate: 'Calculer le score', select: 'Sélectionner...', score: 'Score',
  yes: 'Oui', no: 'Non', notApplicable: 'Sans objet',
  speaking: 'Expression orale', listening: 'Compréhension de l\'oral', reading: 'Lecture', writing: 'Écriture',
  title: 'Votre Score CRS', subtitle: 'Estimation du Système de Classement Global',
  strong: 'Score fort — compétitif pour les tirages généraux',
  moderate: 'Score modéré — compétitif pour les tirages par catégorie',
  below: 'En dessous des seuils typiques — des stratégies sont disponibles',
  improve: 'Amélioration nécessaire — considérez le PNP, la langue ou l\'éducation',
  coreLabel: 'Capital Humain Principal', spouseLabel: 'Facteurs du Conjoint',
  skillLabel: 'Transférabilité des Compétences', additionalLabel: 'Points Supplémentaires',
  ageLabel: 'Âge', educationLabel: 'Éducation', firstLangLabel: 'Première langue',
  secondLangLabel: 'Deuxième langue', canWorkLabel: 'Expérience de travail canadienne',
  spouseEduLabel: 'Éducation', spouseLangLabel: 'Langue', spouseWorkLabel: 'Travail canadien',
  bonusTitle: 'Vous souhaitez améliorer votre score?',
  bonusDesc: 'Continuez vers la consultation complète et recevez une stratégie personnalisée bonus.',
  continueBtn: 'Continuer la Consultation (+ Bonus)', declineBtn: 'Pas maintenant, merci',
  declineNote: 'Un de nos conseillers vous contactera sous peu.',
  disclaimer: 'Cet outil est à titre indicatif uniquement.',
};

const PT = {
  stepOf: 'Etapa {current} de {total}', back: '← Voltar', cancel: '← Cancelar', next: 'Continuar →',
  calculate: 'Calcular pontuação', select: 'Selecionar...', score: 'Pontuação',
  yes: 'Sim', no: 'Não', notApplicable: 'Não aplicável',
  speaking: 'Fala', listening: 'Compreensão auditiva', reading: 'Leitura', writing: 'Escrita',
  title: 'Sua Pontuação CRS', subtitle: 'Estimativa do Sistema de Classificação Abrangente',
  strong: 'Pontuação forte — competitiva para sorteios gerais',
  moderate: 'Pontuação moderada — competitiva para sorteios por categoria',
  below: 'Abaixo dos cortes típicos — estratégias disponíveis',
  improve: 'Melhoria necessária — considere PNP, idioma ou educação',
  coreLabel: 'Capital Humano Principal', spouseLabel: 'Fatores do Cônjuge',
  skillLabel: 'Transferência de Habilidades', additionalLabel: 'Pontos Adicionais',
  ageLabel: 'Idade', educationLabel: 'Educação', firstLangLabel: 'Primeiro idioma',
  secondLangLabel: 'Segundo idioma', canWorkLabel: 'Experiência de trabalho canadense',
  spouseEduLabel: 'Educação', spouseLangLabel: 'Idioma', spouseWorkLabel: 'Trabalho canadense',
  bonusTitle: 'Quer melhorar sua pontuação?',
  bonusDesc: 'Continue para a consulta completa e receba uma estratégia personalizada de bônus.',
  continueBtn: 'Continuar para Consulta (+ Bônus)', declineBtn: 'Agora não, obrigado',
  declineNote: 'Um de nossos consultores entrará em contato em breve.',
  disclaimer: 'Esta ferramenta é apenas para orientação geral.',
};

const HE = {
  stepOf: 'שלב {current} מתוך {total}', back: '→ חזרה', cancel: '→ ביטול', next: '← המשך',
  calculate: 'חשב ציון', select: 'בחר...', score: 'ציון',
  yes: 'כן', no: 'לא', notApplicable: 'לא רלוונטי',
  speaking: 'דיבור', listening: 'הבנת הנשמע', reading: 'קריאה', writing: 'כתיבה',
  title: 'ציון ה-CRS שלך', subtitle: 'הערכת מערכת הדירוג המקיפה',
  strong: 'ציון חזק — תחרותי להגרלות כלליות',
  moderate: 'ציון בינוני — תחרותי להגרלות קטגוריאליות',
  below: 'מתחת לסף הטיפוסי — יש אסטרטגיות זמינות',
  improve: 'דרוש שיפור — שקול PNP, שפה או השכלה',
  coreLabel: 'הון אנושי מרכזי', spouseLabel: 'גורמי בן/בת זוג',
  skillLabel: 'העברת כישורים', additionalLabel: 'נקודות נוספות',
  ageLabel: 'גיל', educationLabel: 'השכלה', firstLangLabel: 'שפה ראשונה',
  secondLangLabel: 'שפה שנייה', canWorkLabel: 'ניסיון עבודה קנדי',
  spouseEduLabel: 'השכלה', spouseLangLabel: 'שפה', spouseWorkLabel: 'עבודה קנדית',
  bonusTitle: 'רוצה לשפר את הציון?',
  bonusDesc: 'המשך לייעוץ המלא וקבל אסטרטגיה מותאמת אישית בונוס מהיועצים המורשים שלנו.',
  continueBtn: 'המשך לייעוץ (+ בונוס)', declineBtn: 'לא עכשיו, תודה',
  declineNote: 'אחד מחברי הצוות שלנו ייצור איתך קשר בקרוב.',
  disclaimer: 'כלי זה מיועד להנחיה כללית בלבד.',
};

const AR = {
  stepOf: 'الخطوة {current} من {total}', back: '→ رجوع', cancel: '→ إلغاء', next: '← متابعة',
  calculate: 'احسب النقاط', select: 'اختر...', score: 'النقاط',
  yes: 'نعم', no: 'لا', notApplicable: 'لا ينطبق',
  speaking: 'التحدث', listening: 'الاستماع', reading: 'القراءة', writing: 'الكتابة',
  title: 'نقاط CRS الخاصة بك', subtitle: 'تقدير نظام التصنيف الشامل',
  strong: 'نتيجة قوية — تنافسية للسحوبات العامة',
  moderate: 'نتيجة معتدلة — تنافسية للسحوبات حسب الفئة',
  below: 'أقل من الحدود النموذجية — توجد استراتيجيات متاحة',
  improve: 'يحتاج تحسين — فكر في PNP أو اللغة أو التعليم',
  coreLabel: 'رأس المال البشري الأساسي', spouseLabel: 'عوامل الزوج/الزوجة',
  skillLabel: 'قابلية نقل المهارات', additionalLabel: 'نقاط إضافية',
  ageLabel: 'العمر', educationLabel: 'التعليم', firstLangLabel: 'اللغة الأولى',
  secondLangLabel: 'اللغة الثانية', canWorkLabel: 'الخبرة العملية الكندية',
  spouseEduLabel: 'التعليم', spouseLangLabel: 'اللغة', spouseWorkLabel: 'العمل الكندي',
  bonusTitle: 'هل تريد تحسين نتيجتك؟',
  bonusDesc: 'تابع إلى الاستشارة الكاملة واحصل على استراتيجية شخصية إضافية.',
  continueBtn: 'متابعة الاستشارة (+ مكافأة)', declineBtn: 'ليس الآن، شكراً',
  declineNote: 'سيتواصل معك أحد أعضاء فريقنا قريباً.',
  disclaimer: 'هذه الأداة للإرشاد العام فقط.',
};

// Chrome only — navigation and form words with one unambiguous reading. All
// substantive copy falls back to English per key.
const CHROME_ONLY = {
  zh: { stepOf: '第 {current} 步，共 {total} 步', back: '← 返回', cancel: '← 取消', next: '继续 →', calculate: '计算分数', select: '请选择...', score: '分数', yes: '是', no: '否', notApplicable: '不适用', speaking: '口语', listening: '听力', reading: '阅读', writing: '写作' },
  ru: { stepOf: 'Шаг {current} из {total}', back: '← Назад', cancel: '← Отмена', next: 'Продолжить →', calculate: 'Рассчитать балл', select: 'Выберите...', score: 'Балл', yes: 'Да', no: 'Нет', notApplicable: 'Не применимо', speaking: 'Говорение', listening: 'Аудирование', reading: 'Чтение', writing: 'Письмо' },
  hi: { stepOf: 'चरण {current} / {total}', back: '← पीछे', cancel: '← रद्द करें', next: 'जारी रखें →', calculate: 'स्कोर की गणना करें', select: 'चुनें...', score: 'स्कोर', yes: 'हाँ', no: 'नहीं', notApplicable: 'लागू नहीं', speaking: 'बोलना', listening: 'सुनना', reading: 'पढ़ना', writing: 'लिखना' },
  ko: { stepOf: '{total}단계 중 {current}단계', back: '← 뒤로', cancel: '← 취소', next: '계속 →', calculate: '점수 계산', select: '선택...', score: '점수', yes: '예', no: '아니오', notApplicable: '해당 없음', speaking: '말하기', listening: '듣기', reading: '읽기', writing: '쓰기' },
  tl: { stepOf: 'Hakbang {current} ng {total}', back: '← Bumalik', cancel: '← Kanselahin', next: 'Magpatuloy →', calculate: 'Kalkulahin ang score', select: 'Pumili...', score: 'Score', yes: 'Oo', no: 'Hindi', notApplicable: 'Hindi naaangkop', speaking: 'Pagsasalita', listening: 'Pakikinig', reading: 'Pagbasa', writing: 'Pagsulat' },
  fa: { stepOf: 'مرحله {current} از {total}', back: '→ بازگشت', cancel: '→ لغو', next: '← ادامه', calculate: 'محاسبه امتیاز', select: 'انتخاب کنید...', score: 'امتیاز', yes: 'بله', no: 'خیر', notApplicable: 'کاربرد ندارد', speaking: 'گفتار', listening: 'شنیدار', reading: 'خواندن', writing: 'نوشتن' },
  tr: { stepOf: 'Adım {current} / {total}', back: '← Geri', cancel: '← İptal', next: 'Devam →', calculate: 'Puanı hesapla', select: 'Seçiniz...', score: 'Puan', yes: 'Evet', no: 'Hayır', notApplicable: 'Uygulanamaz', speaking: 'Konuşma', listening: 'Dinleme', reading: 'Okuma', writing: 'Yazma' },
  uk: { stepOf: 'Крок {current} з {total}', back: '← Назад', cancel: '← Скасувати', next: 'Продовжити →', calculate: 'Розрахувати бал', select: 'Оберіть...', score: 'Бал', yes: 'Так', no: 'Ні', notApplicable: 'Не застосовується', speaking: 'Говоріння', listening: 'Аудіювання', reading: 'Читання', writing: 'Письмо' },
  vi: { stepOf: 'Bước {current} / {total}', back: '← Quay lại', cancel: '← Hủy', next: 'Tiếp tục →', calculate: 'Tính điểm', select: 'Chọn...', score: 'Điểm', yes: 'Có', no: 'Không', notApplicable: 'Không áp dụng', speaking: 'Nói', listening: 'Nghe', reading: 'Đọc', writing: 'Viết' },
  ja: { stepOf: 'ステップ {current} / {total}', back: '← 戻る', cancel: '← キャンセル', next: '続ける →', calculate: 'スコアを計算', select: '選択...', score: 'スコア', yes: 'はい', no: 'いいえ', notApplicable: '該当なし', speaking: 'スピーキング', listening: 'リスニング', reading: 'リーディング', writing: 'ライティング' },
  ur: { stepOf: 'مرحلہ {current} از {total}', back: '→ واپس', cancel: '→ منسوخ', next: '← جاری رکھیں', calculate: 'سکور شمار کریں', select: 'منتخب کریں...', score: 'سکور', yes: 'ہاں', no: 'نہیں', notApplicable: 'لاگو نہیں', speaking: 'بولنا', listening: 'سننا', reading: 'پڑھنا', writing: 'لکھنا' },
  it: { stepOf: 'Passo {current} di {total}', back: '← Indietro', cancel: '← Annulla', next: 'Continua →', calculate: 'Calcola punteggio', select: 'Seleziona...', score: 'Punteggio', yes: 'Sì', no: 'No', notApplicable: 'Non applicabile', speaking: 'Produzione orale', listening: 'Ascolto', reading: 'Lettura', writing: 'Scrittura' },
};

const LOCALES = { en: EN, es: ES, fr: FR, pt: PT, he: HE, ar: AR, ...CHROME_ONLY };

const { t, translator, supported } = createI18n(LOCALES);

export { t, translator };
export const SUPPORTED_CRS_LOCALES = supported;
